<script setup lang="ts">
import {onMounted, onUnmounted, ref, Transition} from "vue";
import {Dialog, DialogContent, DialogDescription} from "@/shared/components/ui/dialog/";
import DialogTitle from "@/shared/components/ui/dialog/DialogTitle.vue";
import {useDialog} from "@/shared/components/ui/dialog/use-dialog.ts";
import DialogHeader from "@/shared/components/ui/dialog/DialogHeader.vue";
import {faTimes, faUpRightAndDownLeftFromCenter} from "@fortawesome/pro-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import DialogDetailUrl from "@/shared/components/ui/dialog/DialogDetailUrl.vue";
import {RouteLocationRaw, useRouter} from "vue-router";
import DialogClose from "@/shared/components/ui/dialog/DialogClose.vue";
import {useScreenSize} from "@/shared/composable/screensize.ts";
import {Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle} from "@/shared/components/ui/drawer";
import {useI18n} from "vue-i18n";
import DialogFooter from "@/shared/components/ui/dialog/DialogFooter.vue";

const {_dialog, closeDialog} = useDialog()
const {t} = useI18n()
let result = null

// Make a function that will trigger on keydown
function keydownListener(event: KeyboardEvent) {
  // Assert the key is escape
  if (event.key === "Escape") cancelThisDialog()
}

// Attach event listener on mount
onMounted(() => {
  document.addEventListener("keydown", keydownListener);
});

// Clean up on unmount
onUnmounted(() => {
  document.removeEventListener("keydown", keydownListener);
});
const cancelThisDialog = () => {
  if (result
  ) {
    closeDialog(true, result);
  } else {
    closeDialog(false);
  }
}
const closeThisDialog = (result?: any | null) => {
  closeDialog(true, result)
}
const updateResult = (res?: any | null) => {
  result = res;
}
const router = useRouter();

const openDetailUrl = () => {
  router.push(_dialog.value.detailUrl as RouteLocationRaw)
  closeThisDialog()
}

const {isMobile, isTablet, isDesktop} = useScreenSize()

const toggleDrawer = (open) => {
  if(open == false) {
    cancelThisDialog()
  }
}
function createEmitHandlers() {
  const handlers = {};
  if (_dialog?.value.emits) {
    _dialog.value.emits.forEach((event) => {
      if (event === "process:finished") {
        handlers[event] = (payload) => closeThisDialog(payload);
      } else if (event === "process:updated") {
        handlers[event] = (payload) => updateResult(payload);
      } else if (event === "process:cancelled") {
        handlers[event] = () => cancelThisDialog();
      } else {
        // For other emits, call the handler passed in props if it exists
        handlers[event] = (...args) => {
          const emitHandler = _dialog.value?.props?.[`on${event[0].toUpperCase()}${event.slice(1)}`];
          if (typeof emitHandler === "function") {
            emitHandler(...args);
          } else {
            console.warn(`Unhandled emit: ${event}`, args);
          }
        };
      }
    });
  }
  return handlers;
}
</script>

<template>

  <Drawer v-if="isMobile" :open="_dialog?.component != null" @update:open="toggleDrawer">
    <DrawerContent>
      <DrawerHeader>
        <component v-if="_dialog?.titleComp" :is="_dialog?.titleComp"/>
        <DrawerTitle v-if="_dialog?.title" class="mb-1">{{ _dialog.title }}</DrawerTitle>
        <component v-if="_dialog?.descriptionComp" :is="_dialog?.descriptionComp"/>
        <DrawerDescription v-else-if="_dialog?.description" v-html="_dialog?.description"/>
      </DrawerHeader>
      <div class="p-default pt-0 max-h-[80vh] overflow-y-scroll">
        <component ref="dialogComponent"
                   :is="_dialog?.component"
                   v-bind="_dialog?.props"
                   v-on="createEmitHandlers()"
                   @process:finished="closeThisDialog"
                   @process:updated="updateResult"
                   @process:cancelled="cancelThisDialog" />
      </div>
    </DrawerContent>
  </Drawer>

  <Teleport to="body" v-else>
    <Transition name="modal-fade">
      <Dialog :open="_dialog?.component != null" :modal="true">
        <DialogContent @update:close="cancelThisDialog" class="flex flex-col max-h-[90dvh] focus:outline-0"
                       :class="[_dialog?.size == 'lg' ? 'sm:max-w-[65vw]' : _dialog?.size == 'md'  ? 'sm:max-w-[50vw]' : _dialog?.size == 'sm'  ? 'sm:max-w-[40vw]' : 'sm:max-w-[30vw]', _dialog.title !== undefined || _dialog.description !== undefined ? 'gap-0' : '']"
                       aria-describedby="Dialog">
          <DialogHeader class="flex flex-row justify-between pb-default"
                        :class="_dialog?.description ? '' : 'items-center'">
            <div>
              <component v-if="_dialog?.titleComp" :is="_dialog?.titleComp"/>
              <DialogTitle v-if="_dialog?.title" class="mb-1">{{ _dialog.title }}</DialogTitle>
              <component v-if="_dialog?.descriptionComp" :is="_dialog?.descriptionComp"/>
              <DialogDescription v-else-if="_dialog?.description" v-html="_dialog?.description"/>
            </div>
            <div class="flex align-center justify-center"
                 :class="_dialog.title == undefined && _dialog.description == undefined ? 'absolute top-6 right-6' : ''">
              <DialogDetailUrl v-if="_dialog.detailUrl" @click="openDetailUrl"
                               class="rounded-sm opacity-70 mr-3 ring-offset-white transition-opacity hover:opacity-100 hover:cursor-pointer data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
                <FontAwesomeIcon :icon="faUpRightAndDownLeftFromCenter"/>
              </DialogDetailUrl>
              <DialogClose @click="cancelThisDialog"
                           class="h-fit rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
                <FontAwesomeIcon :icon="faTimes" size="xl"/>
                <span class="sr-only">{{ t('shared.common.close') }}</span>
              </DialogClose>
            </div>
          </DialogHeader>
          <div class="grid gap-default overflow-y-auto overflow-x-hidden px-1">
            <div class="content">
              <component ref="dialogComponent"
                         :is="_dialog?.component"
                         v-bind="_dialog?.props"
                         @process:finished="closeThisDialog"
                         @process:updated="updateResult"
                         @process:cancelled="cancelThisDialog"
              />
            </div>
          </div>
        <DialogFooter v-if="_dialog?.footerComponent">
          <component v-if="_dialog?.footerComponent" :is="_dialog?.footerComponent"/>
        </DialogFooter>
        </DialogContent>
      </Dialog>
    </Transition>
  </Teleport>
</template>

<style>

</style>